import React from 'react'
import { Box, Typography, makeStyles } from '@material-ui/core'

export interface CommissionGoalStep {
  value: number
  label: string
}

interface Props {
  currentValue: number
  steps: CommissionGoalStep[]
  countLabel: string
  title?: string
}

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2, 2),
    border: '1px solid #e2e5e9',
    borderRadius: 6,
    background: '#fff'
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: theme.spacing(2)
  },
  title: { fontWeight: 700, color: '#2a2f36' },
  count: { fontWeight: 800, color: '#c95300', fontVariantNumeric: 'tabular-nums' },
  trackArea: { position: 'relative', height: 48, margin: theme.spacing(1.25, 8, 0) },
  scroll: { overflowX: 'auto', overflowY: 'hidden' },
  track: {
    position: 'absolute',
    top: 9,
    left: 0,
    right: 0,
    height: 7,
    borderRadius: 7,
    background: '#eceef1',
    overflow: 'hidden'
  },
  fill: { height: '100%', borderRadius: 7, background: '#ff6900', transition: 'width 180ms ease' },
  checkpoint: { position: 'absolute', top: 3, transform: 'translateX(-50%)', textAlign: 'center' },
  dot: {
    display: 'block',
    boxSizing: 'border-box',
    width: 19,
    height: 19,
    margin: '0 auto 4px',
    borderRadius: '50%',
    border: '3px solid #cfd3d8',
    background: '#fff'
  },
  reached: { borderColor: '#ff6900', background: '#ff6900', boxShadow: '0 0 0 3px #fff' },
  label: { display: 'block', fontSize: 10, fontWeight: 700, color: '#69717b', whiteSpace: 'nowrap' },
  '@media (max-width: 600px)': {
    header: { alignItems: 'flex-start', flexDirection: 'column', gap: theme.spacing(0.5) },
    trackArea: { marginLeft: theme.spacing(6), marginRight: theme.spacing(6) }
  }
}))

const CommissionGoalProgress: React.FC<Props> = ({ currentValue, steps, countLabel, title = 'Progreso de meta' }) => {
  const classes = useStyles()
  const maxValue = steps[steps.length - 1]?.value || 1
  const progress = Math.min(100, (currentValue / maxValue) * 100)
  const nextStep = steps.find(step => currentValue < step.value)

  return (
    <Box className={classes.root} aria-label={title}>
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>{title}</Typography>
          <Typography variant="caption" color="textSecondary">
            {nextStep
              ? `Faltan ${nextStep.value - currentValue} casos para ${nextStep.label.toLowerCase()}`
              : 'Meta máxima alcanzada'}
          </Typography>
        </Box>
        <Typography className={classes.count}>{countLabel}</Typography>
      </Box>
      <Box className={classes.scroll}>
        <Box className={classes.trackArea} style={{ minWidth: 560 }}>
          <Box className={classes.track}>
            <Box className={classes.fill} style={{ width: `${progress}%` }} />
          </Box>
          {steps.map(step => (
            <Box
              key={`${step.value}:${step.label}`}
              className={classes.checkpoint}
              style={{ left: `${(step.value / maxValue) * 100}%` }}
            >
              <span className={`${classes.dot} ${currentValue >= step.value ? classes.reached : ''}`} />
              <span className={classes.label}>
                {step.value} · {step.label}
              </span>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default CommissionGoalProgress
